import React, { useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faDrumstickBite, 
   
   
  faPlus, 
  faTimes,
  faAward,
  faInfoCircle,
  faArrowRight,
  
  faCut
} from '@fortawesome/free-solid-svg-icons';
import {
  getMeatCategories,
  getMeatsByCategory,
  getMeatById,
} from '../../db';
import { usePriceOverrides } from '../../context/PriceOverridesContext';
import { isPopularCombo as isPopularComboUtil, isSpecialPricedCombo as isSpecialPricedComboUtil } from '../../utils/meatUtils';
import './MeatSelector.css';

const MeatSelector = ({ 
  primaryMeat, 
  primaryMeatVariant,
  secondaryMeat, 
  secondaryMeatVariant,
  onSelectPrimary, 
  onSelectSecondary,
  onSelectPrimaryVariant,
  onSelectSecondaryVariant 
}) => {
  const [activeCategory, setActiveCategory] = useState('popular');
  // Subscribe to override context so this component re-renders when overrides
  // arrive — the direct db reads below will then return overridden prices.
  usePriceOverrides();
  const categories = getMeatCategories();
  
  // State for currently viewing variants
  const [viewingVariantsFor, setViewingVariantsFor] = useState(null);
  
  // Close variants view when meat selection changes
  useEffect(() => {
    setViewingVariantsFor(null);
  }, [primaryMeat, secondaryMeat]);

  const handleMeatClick = (meatId) => {
    const selectedMeat = getMeatById(meatId);
    
    // Check if this meat has variants that require selection (like corned beef or pastrami)
    if (selectedMeat && selectedMeat.variants && selectedMeat.variants.length > 0 && !selectedMeat.isVariant) {
      // Check if it's a meat with required variants: corned beef (1), pastrami (2), Hot Dog (21), or Beef Tongue (18)
      const requiresVariant = selectedMeat.id === 1 || selectedMeat.id === 2 || selectedMeat.id === 21 || selectedMeat.id === 18;
      
      if (requiresVariant) {
        setViewingVariantsFor(meatId);
        return;
      }
      
      // For other meats that don't need variant selection, just select the meat
      if (!primaryMeat) {
        onSelectPrimary(meatId);
      } else {
        onSelectSecondary(meatId);
      }
      return;
    }
    
    // Handle parent-child meat relationships
    if (selectedMeat && selectedMeat.isVariant && selectedMeat.parentId) {
      const parentId = selectedMeat.parentId;
      const variantType = selectedMeat.variantType;
      
      // If this is a variant of the primary meat
      if (parentId === primaryMeat) {
        onSelectPrimaryVariant(variantType);
        setViewingVariantsFor(null);
        return;
      }
      
      // If this is a variant of the secondary meat
      if (parentId === secondaryMeat) {
        onSelectSecondaryVariant(variantType);
        setViewingVariantsFor(null);
        return;
      }
      
      // If it's a new selection
      onSelectPrimary(parentId);
      onSelectPrimaryVariant(variantType);
      setViewingVariantsFor(null);
      return;
    }
    
    // Normal meat selection logic
    // If the meat is already selected as primary, deselect it
    if (meatId === primaryMeat) {
      onSelectPrimary('');
      onSelectPrimaryVariant('');
      
      // Also remove secondary if present
      if (secondaryMeat) {
        onSelectSecondary('');
        onSelectSecondaryVariant('');
      }
      return;
    }
    
    // If the meat is already selected as secondary, make it primary and remove secondary
    if (meatId === secondaryMeat) {
      onSelectPrimary(meatId);
      onSelectPrimaryVariant(secondaryMeatVariant);
      onSelectSecondary('');
      onSelectSecondaryVariant('');
      return;
    }
    
    // If no primary meat is selected, select as primary
    if (!primaryMeat) {
      onSelectPrimary(meatId);
      
      // If this is a meat that requires a variant (corned beef, pastrami, hot dog, beef tongue)
      // immediately prompt for the variant selection
      if (selectedMeat && selectedMeat.variants) {
        const requiresVariant = selectedMeat.id === 1 || selectedMeat.id === 2 || selectedMeat.id === 21 || selectedMeat.id === 18;
        
        if (requiresVariant) {
          setViewingVariantsFor(meatId);
        }
      }
      return;
    }
    
    // Otherwise, set as secondary meat
    onSelectSecondary(meatId);
    
    // If this is a meat that requires a variant (corned beef, pastrami)
    // immediately prompt for the variant selection
    if (selectedMeat && selectedMeat.variants) {
      const requiresVariant = selectedMeat.id === 1 || selectedMeat.id === 2 || selectedMeat.id === 21 || selectedMeat.id === 18;
      
      if (requiresVariant) {
        setViewingVariantsFor(meatId);
      }
    }
  };
  
  const clearSelections = () => {
    onSelectPrimary('');
    onSelectPrimaryVariant('');
    onSelectSecondary('');
    onSelectSecondaryVariant('');
  };

  const handleRemovePrimaryMeat = () => {
    onSelectPrimary('');
    onSelectPrimaryVariant('');
    // If primary is removed, and secondary exists, secondary becomes primary
    if (secondaryMeat) {
      onSelectPrimary(secondaryMeat);
      onSelectPrimaryVariant(secondaryMeatVariant);
      onSelectSecondary('');
      onSelectSecondaryVariant('');
    }
  };

  const handleRemoveSecondaryMeat = () => {
    onSelectSecondary('');
    onSelectSecondaryVariant('');
  };

  const closeVariantSelection = () => {
    setViewingVariantsFor(null);
  };
  
  const handleAddSecondMeat = () => {
    // Focus or scroll to the meat options section
    document.querySelector('.meat-categories').scrollIntoView({ behavior: 'smooth', block: 'start' });
  };
  
  // Local wrappers that use component state
  const isPopularCombo = () => isPopularComboUtil(primaryMeat, secondaryMeat);
  const isSpecialPricedCombo = () => isSpecialPricedComboUtil(primaryMeat, secondaryMeat);
  
  // Debug
  console.log("Primary meat:", primaryMeat, "Primary variant:", primaryMeatVariant, "Type:", typeof primaryMeatVariant);
  console.log("Secondary meat:", secondaryMeat, "Secondary variant:", secondaryMeatVariant, "Type:", typeof secondaryMeatVariant);
  
  // Get details for selected meats
  const primaryMeatDetails = getMeatById(primaryMeat);
  const secondaryMeatDetails = getMeatById(secondaryMeat);
  
  // Get variant names if applicable
  const primaryVariantName = primaryMeatVariant ? 
    primaryMeatDetails?.variants?.find(v => v.id === primaryMeatVariant)?.name :
    null;
    
  const secondaryVariantName = secondaryMeatVariant ? 
    secondaryMeatDetails?.variants?.find(v => v.id === secondaryMeatVariant)?.name :
    null;

  // Show the variants UI when a meat with variants is selected
  const renderVariantSelection = () => {
    if (!viewingVariantsFor) return null;
    
    const meat = getMeatById(viewingVariantsFor);
    if (!meat || !meat.variants) return null;
    
    // Handle special variant naming for Corned Beef/Pastrami/Beef Tongue
    const isCornedOrPastrami = meat.id === 1 || meat.id === 2;
    const isBeefTongue = meat.id === 18;
    
    return (
      <div className="meat-variants-overlay">
        <div className="meat-variants-container">
          <div className="meat-variants-header">
            <h4>Select {meat.name} Type</h4>
            <button className="close-variants" onClick={closeVariantSelection}>
              <FontAwesomeIcon icon={faTimes} />
            </button>
          </div>
          
          <div className="meat-variants-grid">
            {/* We don't need a separate "Regular" option - variants already include it */}
            
            {/* Then add all the variants */}
            {meat.variants.map(variant => {
              // For each variant, create a composite ID to recognize it later
              const variantId = `${meat.id}-${variant.id}`;
              const isSelectedPrimary = primaryMeat === meat.id && primaryMeatVariant === variant.id;
              const isSelectedSecondary = secondaryMeat === meat.id && secondaryMeatVariant === variant.id;
              
              // Display name based on meat type
              let displayName;
              if (isCornedOrPastrami || isBeefTongue) {
                // For corned beef, pastrami, or beef tongue, show the type
                displayName = `${variant.name} ${meat.name}`;
              } else {
                // For Hot Dog and potentially others in the future
                displayName = variant.name;
              }
              
              return (
                <div
                  key={variantId}
                  className={`meat-variant-card ${isSelectedPrimary ? 'selected' : ''} ${isSelectedSecondary ? 'selected-secondary' : ''}`}
                  onClick={() => {
                    // Handle selection based on context
                    if (viewingVariantsFor === primaryMeat) {
                      onSelectPrimaryVariant(variant.id);
                    } else if (!primaryMeat) {
                      onSelectPrimary(meat.id);
                      onSelectPrimaryVariant(variant.id);
                    } else if (viewingVariantsFor === secondaryMeat) {
                      onSelectSecondaryVariant(variant.id);
                    } else {
                      onSelectSecondary(meat.id);
                      onSelectSecondaryVariant(variant.id);
                    }
                    setViewingVariantsFor(null);
                  }}
                >
                  <FontAwesomeIcon icon={faCut} className="meat-icon" />
                  <div className="meat-text">
                    <span className="meat-name">{displayName}</span>
                    <span className="meat-price">
                      {variant.price_adjustment > 0 
                        ? `$${(meat.base_price + variant.price_adjustment).toFixed(2)}` 
                        : `$${meat.base_price.toFixed(2)}`}
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    );
  };
  
  return (
    <div className="meat-selector-container">
      {/* Meat Categories Tabs */}
      <div className="meat-categories">
        {categories.map(category => (
          <div 
            key={category}
            className={`category-tab ${activeCategory === category ? 'active' : ''}`}
            onClick={() => setActiveCategory(category)}
          >
            {category.charAt(0).toUpperCase() + category.slice(1)}
          </div>
        ))}
      </div>
      
      {/* Meat Grid */}
      <div className="meat-grid">
        {getMeatsByCategory(activeCategory).map(meat => {
          // Skip rendering variants directly in the main grid
          if (meat.isVariant) return null;
          
          // Check if this meat is selected
          const isPrimary = meat.id === primaryMeat;
          const isSecondary = meat.id === secondaryMeat;
          
          // Check if this is a special meat with variants
          // Show variants for meats that need variant selection (corned beef, pastrami, hot dog, beef tongue)
          const hasVariants = meat.variants && meat.variants.length > 0 && (meat.id === 1 || meat.id === 2 || meat.id === 21 || meat.id === 18);
          
          return (
            <div
              key={meat.id}
              className={`meat-card ${isPrimary ? 'selected' : ''} ${isSecondary ? 'selected-secondary' : ''} ${hasVariants ? 'has-variants' : ''}`}
              onClick={() => handleMeatClick(meat.id)}
            >
              {/* Badge removed as requested */}
              <FontAwesomeIcon icon={meat.icon || faDrumstickBite} className="meat-icon" />
              <div className="meat-text">
                <span className="meat-name">{meat.name}</span>
                <span className="meat-price">
                  {(() => {
                    let finalPrice = meat.base_price;
                    // Check if the current meat card is the selected primary meat and has a variant selected
                    if (isPrimary && primaryMeatVariant && meat.variants && meat.id === primaryMeat) {
                      const variantDetails = meat.variants.find(v => v.id === parseInt(primaryMeatVariant));
                      if (variantDetails) {
                        finalPrice = meat.base_price + variantDetails.price_adjustment;
                      }
                    }
                    // Check if the current meat card is the selected secondary meat and has a variant selected
                    else if (isSecondary && secondaryMeatVariant && meat.variants && meat.id === secondaryMeat) {
                      const variantDetails = meat.variants.find(v => v.id === parseInt(secondaryMeatVariant));
                      if (variantDetails) {
                        finalPrice = meat.base_price + variantDetails.price_adjustment;
                      }
                    }
                    return `$${finalPrice.toFixed(2)}`;
                  })()}
                </span>
              </div>
              {hasVariants && (
                <div className="variants-indicator">
                  <FontAwesomeIcon icon={faArrowRight} />
                </div>
              )}
            </div>
          );
        })}
      </div>
      
      {/* Selection Summary */}
      {primaryMeat && (
        <div className="meat-selection-summary">
          <div className="summary-header">
            <h4 className="summary-title">Your Selection</h4>
            <button className="clear-selection" onClick={clearSelections}>
              <FontAwesomeIcon icon={faTimes} /> Clear
            </button>
          </div>
          
          <div className="selected-meats">
            <div className="selected-meat-pill primary">
              <FontAwesomeIcon icon={primaryMeatDetails?.icon || faDrumstickBite} className="selected-meat-icon" />
              <span className="selected-meat-name">
                {/* Show proper naming based on meat type */}
                {parseInt(primaryMeat) === 13 && primaryVariantName
                  ? primaryVariantName
                  : (parseInt(primaryMeat) === 1 || parseInt(primaryMeat) === 2 || parseInt(primaryMeat) === 21 || parseInt(primaryMeat) === 18) && primaryVariantName
                    ? `${primaryVariantName} ${primaryMeatDetails?.name}`
                    : primaryMeatDetails?.name}
              </span>
              {primaryMeatDetails?.variants && primaryMeatDetails.variants.length > 0 &&
                (primaryMeatDetails.id === 1 || primaryMeatDetails.id === 2 || primaryMeatDetails.id === 13 || primaryMeatDetails.id === 21 || primaryMeatDetails.id === 18) && (
                <button
                  className="change-variant-btn"
                  onClick={() => setViewingVariantsFor(primaryMeat)}
                >
                  Change
                </button>
              )}
              <button
                className="remove-meat-btn"
                onClick={handleRemovePrimaryMeat}
                aria-label="Remove primary meat"
              >
                <FontAwesomeIcon icon={faTimes} />
              </button>
            </div>
            
            {secondaryMeat ? (
              <div className="selected-meat-pill secondary">
                <FontAwesomeIcon icon={secondaryMeatDetails?.icon || faDrumstickBite} className="selected-meat-icon" />
                <span className="selected-meat-name">
                  {/* Show proper naming based on meat type */}
                  {parseInt(secondaryMeat) === 13 && secondaryVariantName
                    ? secondaryVariantName
                    : (parseInt(secondaryMeat) === 1 || parseInt(secondaryMeat) === 2 || parseInt(secondaryMeat) === 21 || parseInt(secondaryMeat) === 18) && secondaryVariantName
                      ? `${secondaryVariantName} ${secondaryMeatDetails?.name}`
                      : secondaryMeatDetails?.name}
                </span>
                {secondaryMeatDetails?.variants && secondaryMeatDetails.variants.length > 0 &&
                  (secondaryMeatDetails.id === 1 || secondaryMeatDetails.id === 2 || secondaryMeatDetails.id === 13 || secondaryMeatDetails.id === 21 || secondaryMeatDetails.id === 18) && (
                  <button
                    className="change-variant-btn"
                    onClick={() => setViewingVariantsFor(secondaryMeat)}
                  >
                    Change
                  </button>
                )}
                <button
                  className="remove-meat-btn"
                  onClick={handleRemoveSecondaryMeat}
                  aria-label="Remove secondary meat"
                >
                  <FontAwesomeIcon icon={faTimes} />
                </button>
              </div>
            ) : (
              <button className="add-button" onClick={handleAddSecondMeat}>
                <FontAwesomeIcon icon={faPlus} />
                Add Second Meat
              </button>
            )}
          </div>
          
          {/* Show combo note if a second meat is selected */}
          {secondaryMeat && (
            <div className="combo-note">
              <h4>
                <FontAwesomeIcon icon={(isPopularCombo() || isSpecialPricedCombo()) ? faAward : faInfoCircle} />
                {isPopularCombo() ? 'Perfect Combo!' : isSpecialPricedCombo() ? 'Special Combo!' : 'Combo Selection'}
              </h4>
              <p>
                {isPopularCombo() 
                  ? "You've selected our most popular combination - Corned Beef & Pastrami!"
                  : isSpecialPricedCombo()
                    ? "You've selected our special Corned Beef & Chop Liver combo - $17.99!"
                    : "You can combine any two meats at no extra charge. Higher price applies."}
              </p>
            </div>
          )}
        </div>
      )}
      
      {/* Info about combining meats - only show when no combo is selected */}
      {!secondaryMeat && (
        <div className="combo-note">
          <h4>
            <FontAwesomeIcon icon={faInfoCircle} />
            Build Your Own Combo
          </h4>
          <p>
            Select any two meats to create your own custom combination.
            Most popular: Corned Beef & Pastrami.
          </p>
        </div>
      )}
      
      {/* Variants selector overlay */}
      {renderVariantSelection()}
    </div>
  );
};

export default MeatSelector;